import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scale } from "lucide-react";
import { toast } from "sonner";

/**
 * Sprint 7 — Agency-Rechtsfelder.
 *
 * Lädt agency-Profil über GET /api/agency/profile, schreibt über PATCH /api/agency/profile.
 * Diese URLs werden im AgencyPortal-Footer bevorzugt vor revenioo.ai-Rechtsseiten verwendet.
 */
export default function AgencyLegalCard() {
  const [profile, setProfile] = useState(null);
  const [imprint, setImprint] = useState("");
  const [privacy, setPrivacy] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/agency/profile");
        if (cancelled) return;
        setProfile(r.data);
        setImprint(r.data?.imprint_url || "");
        setPrivacy(r.data?.privacy_url || "");
      } catch (e) {
        // 403 oder fehlende Rechte → Card bleibt verborgen
        setProfile(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/agency/profile", {
        imprint_url: imprint.trim(),
        privacy_url: privacy.trim(),
      });
      setProfile(r.data);
      toast.success("Agentur-Rechtslinks gespeichert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) return null;

  return (
    <Card className="p-6 border-slate-200" data-testid="agency-legal-card">
      <div className="flex items-center gap-2 mb-3">
        <Scale className="w-4 h-4 text-orange-600" />
        <h2 className="text-lg font-semibold text-slate-900">Rechtliches</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Diese Links werden im Agentur-Portal-Footer bevorzugt angezeigt. Sind sie leer, fallen wir
        auf die Rechtsseiten von revenioo.ai zurück.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="agency-imprint-url" className="text-sm">Impressum-URL der Agentur</Label>
          <Input
            id="agency-imprint-url"
            data-testid="agency-imprint-url"
            value={imprint}
            onChange={(e) => setImprint(e.target.value)}
            placeholder="https://ihre-agentur.de/impressum"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="agency-privacy-url" className="text-sm">Datenschutz-URL der Agentur</Label>
          <Input
            id="agency-privacy-url"
            data-testid="agency-privacy-url"
            value={privacy}
            onChange={(e) => setPrivacy(e.target.value)}
            placeholder="https://ihre-agentur.de/datenschutz"
            className="mt-1"
          />
        </div>
      </div>
      <div className="mt-4">
        <Button
          data-testid="agency-legal-save"
          onClick={save}
          disabled={saving}
          className="bg-orange-600 hover:bg-orange-700 text-white"
        >
          {saving ? "Speichern …" : "Speichern"}
        </Button>
      </div>
    </Card>
  );
}
